import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ConsumerLayout from './ConsumerLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Radar, Store, Clock, Tag, ShoppingCart, RefreshCw, MapPin, AlertCircle, Loader2, Percent, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { rshd, dacRetailers } from '../../utils/api';
import ProductCard from './ProductCard';

// Helper to create store slug
const slugify = (text) => {
  if (!text) return '';
  return text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
};

export default function ConsumerRadar({ user, onLogout }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [retailers, setRetailers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [hasDeliveryLocation, setHasDeliveryLocation] = useState(false);

  const categories = [
    'All Categories', 'Fruits', 'Vegetables', 'Dairy & Eggs', 'Meat & Poultry',
    'Seafood', 'Bakery', 'Deli', 'Frozen Foods', 'Beverages',
    'Snacks & Candy', 'Breakfast & Cereal', 'Canned & Jarred Goods',
    'Pasta, Rice & Grains', 'Oils, Sauces & Spices', 'Baking Supplies',
    'International Foods', 'Baby & Kids', 'Health & Wellness',
    'Household & Cleaning', 'Personal Care', 'Pet Supplies', 'Miscellaneous'
  ];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Get DACDRLP-List to check if user has retailers
      const retailersResponse = await dacRetailers.list();
      const retailerData = retailersResponse.data;
      const activeRetailers = (retailerData.retailers || []).filter(r => !r.manually_removed);
      setRetailers(activeRetailers);
      setHasDeliveryLocation(!!retailerData.dacsai_center);
      
      // Get RSHDs - filter to only show from retailers in DACDRLP-List
      const rshdResponse = await rshd.list();
      const allItems = rshdResponse.data || [];
      
      // Filter to only show items from retailers in user's DACDRLP-List
      const retailerIds = new Set(activeRetailers.map(r => r.drlp_id));
      const filteredItems = allItems.filter(item => retailerIds.has(item.drlp_id));
      
      setItems(filteredItems);
    } catch (error) {
      console.error('Failed to load radar data:', error);
      toast.error('Failed to load deals');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
    toast.success('Deals refreshed');
  };

  // Filter items by category
  const filteredItems = selectedCategory === 'all' 
    ? items 
    : items.filter(item => item.category === selectedCategory);

  // Get discount badge color and name
  const getDiscountBadge = (level) => {
    switch(level) {
      case 1: return { text: '50% OFF', color: 'bg-green-500', name: 'Sizzling' };
      case 2: return { text: '60% OFF', color: 'bg-yellow-500', name: 'Hot' };
      case 3: return { text: '75% OFF', color: 'bg-red-500', name: 'Sizzling Hot' };
      default: return { text: 'DEAL', color: 'bg-emerald-500', name: 'Deal' };
    }
  };

  // Get retailer name by ID
  const getRetailerName = (drlpId) => {
    const retailer = retailers.find(r => r.drlp_id === drlpId);
    return retailer?.drlp_name || 'Unknown Store';
  };

  if (loading) {
    return (
      <ConsumerLayout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
        </div>
      </ConsumerLayout>
    );
  }

  return (
    <ConsumerLayout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <div className="flex items-center gap-2">
              <Radar className="w-6 h-6 text-emerald-600" />
              <h1 className="text-2xl font-bold text-gray-900">Deal Radar</h1>
            </div>
            <p className="text-gray-600 mt-1">
              Sizzling hot deals from your {retailers.length} local retailers
            </p>
          </div>
          <div className="flex gap-2">
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.slice(1).map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button 
              variant="outline" 
              size="icon"
              onClick={handleRefresh}
              disabled={refreshing}
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {/* No Location Warning */}
        {!hasDeliveryLocation && (
          <Alert className="bg-amber-50 border-amber-200">
            <AlertCircle className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-800">
              Set your delivery location in <strong>Settings</strong> to see deals from nearby retailers.
            </AlertDescription>
          </Alert>
        )}

        {/* No Retailers Warning */}
        {hasDeliveryLocation && retailers.length === 0 && (
          <Alert className="bg-blue-50 border-blue-200">
            <Store className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-blue-800">
              No retailers in your area yet. Visit <strong>My Retailers</strong> to add stores manually.
            </AlertDescription>
          </Alert>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-emerald-600">{filteredItems.length}</p>
              <p className="text-sm text-gray-500">Active Deals</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-emerald-600">{retailers.length}</p>
              <p className="text-sm text-gray-500">Your Retailers</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-orange-500">
                {filteredItems.filter(i => i.discount_level >= 2).length}
              </p>
              <p className="text-sm text-gray-500">Hot Deals+</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-red-500">
                {filteredItems.filter(i => i.discount_level === 3).length}
              </p>
              <p className="text-sm text-gray-500">Sizzling Hot Deals</p>
            </CardContent>
          </Card>
        </div>

        {/* Deals Grouped by Retailer */}
        {filteredItems.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Radar className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Deals Available</h3>
              <p className="text-gray-500">
                {selectedCategory === 'all' 
                  ? 'Check back later for new deals from your local retailers'
                  : `No ${selectedCategory} deals right now. Try a different category.`}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-8">
            {/* Group items by retailer */}
            {(() => {
              // Group filtered items by drlp_id
              const groupedByRetailer = filteredItems.reduce((groups, item) => {
                const key = item.drlp_id;
                if (!groups[key]) {
                  groups[key] = {
                    drlp_id: item.drlp_id,
                    drlp_name: item.drlp_name || getRetailerName(item.drlp_id),
                    drlp_address: item.drlp_address || '',
                    items: []
                  };
                }
                groups[key].items.push(item);
                return groups;
              }, {});

              // Get retailer info for any retailers in DACDRLP-List that have no RSHDs
              const retailersWithDeals = new Set(Object.keys(groupedByRetailer));
              const retailersWithoutDeals = retailers.filter(r => !retailersWithDeals.has(r.drlp_id));

              return (
                <>
                  {/* Retailers WITH RSHDs */}
                  {Object.values(groupedByRetailer).map((retailerGroup) => (
                    <div key={retailerGroup.drlp_id} className="space-y-4">
                      {/* Store Header */}
                      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="bg-emerald-600 text-white p-2 rounded-lg">
                              <Store className="w-5 h-5" />
                            </div>
                            <div>
                              <h2 className="text-lg font-bold text-gray-900">{retailerGroup.drlp_name}</h2>
                              {retailerGroup.drlp_address && (
                                <p className="text-sm text-gray-600 flex items-center gap-1">
                                  <MapPin className="w-3 h-3" />
                                  {retailerGroup.drlp_address}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge className="bg-emerald-100 text-emerald-700">
                              {retailerGroup.items.length} {retailerGroup.items.length === 1 ? 'Deal' : 'Deals'}
                            </Badge>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => navigate(`/store/${slugify(retailerGroup.drlp_name)}/storefront`)}
                              className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-100"
                            >
                              View Store
                              <ExternalLink className="w-3 h-3 ml-1" />
                            </Button>
                          </div>
                        </div>
                      </div>

                      {/* Store's RSHD Items */}
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pl-4 border-l-4 border-emerald-200">
                        {retailerGroup.items.map((item) => (
                          <ProductCard
                            key={item.id}
                            item={item}
                            onAddToCart={() => toast.success(`Added ${item.name} to cart`)}
                            showStoreName={false}
                            variant="default"
                          />
                        ))}
                      </div>
                    </div>
                  ))}

                  {/* Retailers WITHOUT RSHDs (only show if there are some with deals) */}
                  {retailersWithoutDeals.length > 0 && Object.keys(groupedByRetailer).length > 0 && (
                    <div className="space-y-4 mt-8">
                      <h3 className="text-lg font-semibold text-gray-700">Other Retailers in Your List</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {retailersWithoutDeals.map((retailer) => (
                          <div key={retailer.drlp_id} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                            <div className="flex items-center gap-3">
                              <div className="bg-gray-400 text-white p-2 rounded-lg">
                                <Store className="w-5 h-5" />
                              </div>
                              <div>
                                <h4 className="font-medium text-gray-900">{retailer.drlp_name}</h4>
                                <p className="text-sm text-gray-500">No deals posted yet</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        )}
      </div>
    </ConsumerLayout>
  );
}
